/**
 * suggestProjectIntent — context-aware pre-suggester for the three
 * cross-cutting Project Intent fields (workload profile / nuclear
 * interest / priority) surfaced in the Rerun + New Report wizards.
 *
 * Six resolution tiers per field — first non-null wins:
 *   1. prior-report       (~95%)  — most-recent successful report's value
 *   2. document-extracted (~85%)  — sales-deck / deal-packet / project-brief
 *                                   text contains a recognisable intent phrase
 *   3. site-context       (~70%)  — derived from SED (nuclear distance, …)
 *   4. archetype-default  (~60%)  — COMPANY_ARCHETYPES.{archetype}.defaultIntent
 *   5. dam-default        (~50%)  — DAM-emitted per-archetype default
 *   6. fallback           (~30%)  — workload='mixed', nuclear='considering',
 *                                   priority='cost'. Never the only path.
 *
 * Pure function — no side effects, no store reads, no Firestore calls.
 * Caller supplies everything in `input`. Returns one IntentSuggestion per
 * field with `{value, source, rationale, confidence}`. Never throws — any
 * tier may be silently skipped (e.g. `damContext === undefined`).
 *
 * Rationales are kept ≤140 chars, source-cited where possible
 * ("Prior report set this 3 days ago" / "Sales deck mentions BTM nuclear"
 * / "Within 50 km of an NRC reactor").
 */

import type { SiteEntity } from '../types/site';
import type { ProjectIntent, SiteDocument, SiteRagReport } from '../types/siteRag';
import type { CompanyArchetype } from '../types/company';
import type { ReactorDesignClassId } from '../config/reactorDesignCatalog';
import type { WorkloadProfile } from './workloadProfile';
import {
  extractIntentFromDocs as extractFromDocsImpl,
  type DocIntentExtraction,
} from './intentDocExtractor';

// Re-export the document parser so existing IP6 callers keep working
// after the helper moved out of this file.
export { extractIntentFromDocs } from './intentDocExtractor';
export type { DocIntentExtraction } from './intentDocExtractor';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type IntentSuggestionSource =
  | 'prior-report'
  | 'archetype-default'
  | 'site-context'
  | 'document-extracted'
  | 'portfolio-precedent'
  | 'llm-inferred'
  | 'dam-default'
  | 'fallback';

export interface IntentSuggestion<T> {
  value: T;
  source: IntentSuggestionSource;
  rationale: string;     // ≤140 chars
  confidence: number;    // 0-100
}

export type WorkloadProfileId = WorkloadProfile;
export type NuclearInterest = 'primary' | 'considering' | 'no';
export type Priority = 'cost' | 'speed' | 'sustainability' | 'resilience';
/** Mirror of `ProjectIntent.coolingStrategy` (IT-side) — see types/siteRag.ts. */
export type CoolingStrategy = 'air' | 'liquid' | 'hybrid';
/** Mirror of `BopHeatRejection` (cold-end / heat-rejection) minus `'unknown'`. */
export type BopHeatRejectionId =
  | 'dry-cooler'
  | 'wet-cooling-tower'
  | 'hybrid'
  | 'once-through'
  | 'absorption-cchp'
  | 'free-cooling';
/**
 * Cooling-GENERATION machine (#157) — names the MACHINE that produces cooling,
 * distinct from the IT-side `coolingStrategy` AND the cold-end sink
 * (`bopHeatRejection`). Mirrors `CoolingGeneration` in `lib/powerStack.ts`
 * minus `'unknown'` (the suggester only emits decided values).
 */
export type CoolingGenerationId =
  | 'absorption-thermal'
  | 'steam-driven-mechanical'
  | 'vapour-compression-electric';
/** Mirror of `ProjectIntent.timeline` bucket minus `'undecided'`. */
export type TimelineBucketId = 'under-2yr' | '2-5yr' | 'over-5yr';
/**
 * #34 — firm-capacity (ELCC) accreditation target. Mirrors
 * `ProjectIntent.capacityCreditIntent.target`. The suggestion carries the
 * inner enum (not the full object) so the ranker / UI can format + apply it
 * uniformly (mirrors the other categorical fields).
 */
export type CapacityCreditTargetId = 'none' | 'partial' | 'firm';
/** #34 — PPA / revenue structure. Mirrors `ProjectIntent.offtake.structure`. */
export type OfftakeStructureId = 'merchant' | 'contracted' | 'hybrid';
/** #34 — carbon commitment. Mirrors `ProjectIntent.carbonCommitment`. */
export type CarbonCommitmentId =
  | 'none'
  | 're100'
  | '24-7-cfe'
  | 'net-zero-2030'
  | 'net-zero-2050'
  | 'custom';
/** #34 — reactor design class. Re-export of the catalog id so callers reading
 *  the suggestion don't need a second import. */
export type ReactorDesignClassSuggestionId = ReactorDesignClassId;

export interface SuggestedProjectIntent {
  workloadProfile: IntentSuggestion<WorkloadProfileId>;
  nuclearInterest: IntentSuggestion<NuclearInterest>;
  priority: IntentSuggestion<Priority>;
  /**
   * S1 — broadened deterministic coverage. These fields only carry a
   * suggestion when a tier fired; absent (`undefined`) means "no signal,
   * leave the form field as-is". Additive — existing 3-field callers are
   * unaffected (they never read these keys).
   */
  coolingStrategy?: IntentSuggestion<CoolingStrategy>;
  bopHeatRejection?: IntentSuggestion<BopHeatRejectionId>;
  /**
   * #157 — cooling-GENERATION machine (absorption-thermal /
   * steam-driven-mechanical / vapour-compression-electric). Distinct from
   * `coolingStrategy` (IT-side) and `bopHeatRejection` (cold-end sink). Only
   * present when the deterministic generation tier fired. Additive — existing
   * callers that don't read this key are unaffected.
   */
  coolingGeneration?: IntentSuggestion<CoolingGenerationId>;
  /** Exact in-service date (ISO yyyy-mm-dd) when the precedent/site signal
   *  is precise enough; otherwise `timeline` carries the coarse bucket. */
  inServiceDate?: IntentSuggestion<string>;
  timeline?: IntentSuggestion<TimelineBucketId>;
  /** Exact MW suggestion (clamped to the form's 0.1–10000 bounds). */
  powerRequirementMW?: IntentSuggestion<number>;
  /**
   * #34 — DECIDE-NEXT BTM decision fields, inferred so the path / verdict
   * suggester can offer a value when the user "genuinely does not know".
   * Each only carries a suggestion when a real signal fired (non-`fallback`);
   * a bare default uses `source: 'fallback'` so the UI can hide it. Additive —
   * existing callers that don't read these keys are unaffected.
   *
   * The value is the INNER enum (`capacityCreditIntent.target`,
   * `offtake.structure`) — not the full ProjectIntent object — so the ranker /
   * UI can format + apply it the same way as the other categorical fields.
   */
  capacityCreditIntent?: IntentSuggestion<CapacityCreditTargetId>;
  /** Compatibility slot for callers; no suggestion is emitted without a
   * verified commercial-evidence input. Preferences, cohorts and old reports
   * cannot establish a signed contract or even a merchant revenue posture. */
  offtake?: IntentSuggestion<OfftakeStructureId>;
  carbonCommitment?: IntentSuggestion<CarbonCommitmentId>;
  reactorDesignClass?: IntentSuggestion<ReactorDesignClassSuggestionId>;
}

export interface DamIntentContext {
  /** DAM-emitted default intents keyed by archetype id (e.g. 'hyperscaler').
   *  Each value is a partial ProjectIntent. Currently the DAM build doesn't
   *  emit this — but the field exists so IP1 is forward-compatible. */
  defaultIntentByArchetype: Record<string, Partial<ProjectIntent>>;
}

export interface SuggestProjectIntentInput {
  site?: SiteEntity;
  /** Most recent first. Used by the prior-report tier. */
  recentReports?: SiteRagReport[];
  /** Site documents — used by the document-extracted tier when they carry
   *  `sales-deck` / `deal-packet` / `project-brief` / `one-pager` tags. */
  documents?: SiteDocument[];
  archetype?: CompanyArchetype;
  damContext?: DamIntentContext;
  /** Optional explicit nuclear-proximity hint, used when the caller has
   *  computed it outside the SED (e.g. lat/lng + NRC dataset). When unset,
   *  the suggester only fires the site-context tier from `site.evidenceReadiness`
   *  / passed-in fields. Distance in kilometres to the nearest NRC reactor. */
  nearestReactorKm?: number;
  /** Optional: site has Early Site Permit / ESP-related ADAMS docs. */
  hasEspDocs?: boolean;

  // ─────── #157 — cooling-GENERATION machine signals ───────
  // Pure inputs the caller plumbs in (mirrors the `nearestReactorKm` /
  // `hasEspDocs` optional-input convention — the suggester never reads the
  // store). Absent ⇒ the cooling-generation tier is skipped (never fabricated).
  /** On-site THERMAL/STEAM generation present (gas / SMR / nuclear / recip /
   *  gas-turbine in the power mix). Drives the absorption-vs-electric choice —
   *  the caller derives it (e.g. via `phaseHasOnSiteThermalGen`). */
  hasOnSiteThermalGen?: boolean;
  /** The project intends to RE-USE waste heat (CCHP / waste-heat capture). When
   *  true alongside on-site thermal gen, the machine is absorption/steam-driven
   *  rather than electric. */
  wasteHeatReuseIntent?: boolean;

  // ─────── S1 — deterministic site / SED climate signals ───────
  // Pure inputs the caller plumbs in from the SED snapshot (the suggester
  // never reads Firestore). Mirror the `nearestReactorKm` / `hasEspDocs`
  // convention: absent ⇒ that tier is skipped (never fabricated).
  /** Site climate signals — from the report `datasets.climate` snapshot
   *  (`ClimateData`). Drives the cooling-strategy + heat-rejection tier. */
  climate?: {
    /** Annual average dry-bulb, °C. */
    avgTempC?: number;
    /** Local water-stress band — drives wet vs dry heat-rejection choice. */
    waterStress?: 'Low' | 'Moderate' | 'High';
  };
  /** Legacy context only: gross site acreage is not a usable-land or resource
   *  determination and never supplies a power-requirement suggestion. */
  siteAcreage?: number;
  /** Already-resolved workload profile for the site (from intent or the
   *  workload tier). Lets the cooling / power tiers reuse the workload
   *  signal without recomputing it. When absent, the tiers read the
   *  workload suggestion computed in the same pass. */
  workloadProfileHint?: WorkloadProfileId;
  /**
   * #34 — already-resolved nuclear stance from the current intent. Lets the
   * reactor-class / ELCC tiers honour a stance the USER has already declared
   * (e.g. `nuclearInterest: 'primary'`) even when no archetype / prior-report
   * tier would otherwise surface it. Treated as a real signal (mirrors
   * `workloadProfileHint`). When absent, those tiers read the nuclear
   * suggestion computed in the same pass. `'undecided'` is ignored. */
  nuclearInterestHint?: NuclearInterest;
  /**
   * #34 — already-resolved priority from the current intent. Lets the
   * offtake / carbon / ELCC tiers honour a USER-declared priority (e.g.
   * `priority: 'sustainability'`) directly. `'undecided'` is ignored. */
  priorityHint?: Priority;

  // ─────── S1 — portfolio-precedent cohort ───────
  /**
   * Pre-resolved cohort of comparable sites' intents — the caller runs
   * `findSimilarSites` (services/siteCorpus.ts) + looks up each match's
   * `rerunConfig.projectIntent`, then passes the array here. Keeping the
   * cohort resolution in the caller keeps this function pure + unit-testable
   * (mirrors `compareToPrecedents`'s input-pure contract). The
   * `portfolio-precedent` tier reads the cohort mode (most-common value)
   * per field. When fewer than `MIN_PRECEDENT_COHORT` entries carry a
   * field, that field's precedent tier is suppressed.
   */
  precedentIntents?: Array<Partial<ProjectIntent>>;
  /** Human-readable cohort label for the rationale (e.g. "SMR · 200-300 MW
   *  · USA"). Callers build this themselves — `precedentComparison.ts` was
   *  removed as dormant (never wired); restore from 9cb2c600c if a cohort surface
   *  is ever built. */
  precedentCohortLabel?: string;

  /**
   * S1 — USER ALWAYS WINS guard. The site's current intent + its
   * `fieldSource` provenance. Any field whose provenance is a
   * user-authored tier (`'user'` / `'accepted'` / `'manual'`) is NEVER
   * re-suggested — the suggestion for that field is suppressed entirely so
   * the banner can't offer to overwrite a value the user owns. Fields
   * carrying a machine tier ('prior-report' / 'archetype-default' / …) or
   * no provenance at all remain eligible. Optional — when absent, every
   * field is eligible (legacy callers keep their behaviour).
   */
  currentIntent?: Pick<ProjectIntent, 'fieldSource'>;
}

/** Provenance tiers that mean the USER owns the field — never overwrite. */
const USER_AUTHORED_SOURCES: ReadonlySet<string> = new Set([
  'user',
  'accepted',
  'manual',
]);

/** True when `field`'s provenance marks it user-authored (USER WINS). The
 *  suggester suppresses suggestions for such fields so the apply-all /
 *  per-field chip can never overwrite a value the user set themselves. */
function isUserAuthored(
  currentIntent: Pick<ProjectIntent, 'fieldSource'> | undefined,
  field: keyof NonNullable<ProjectIntent['fieldSource']>,
): boolean {
  const src = currentIntent?.fieldSource?.[field];
  return typeof src === 'string' && USER_AUTHORED_SOURCES.has(src);
}

/** Minimum cohort size before the portfolio-precedent tier fires for a
 *  field — the same philosophy as the removed `precedentComparison.ts`
 *  (dormant, deleted; see 9cb2c600c), but
 *  intentionally looser (3) because intent fields are categorical, not
 *  continuous, so a smaller sample is still informative. */
export const MIN_PRECEDENT_COHORT = 3;

// ─────────────────────────────────────────────────────────────────────────────
// Archetype defaults — IP1 declares them inline so the suggester is
// self-contained. (The spec mentions adding `defaultIntent` to each
// CompanyArchetypeMeta; doing it inline here keeps the change footprint
// to one file. Future polish can hoist these onto the archetype config
// without changing the helper's contract.)
// ─────────────────────────────────────────────────────────────────────────────

interface ArchetypeDefaultIntent {
  workloadProfile?: WorkloadProfileId;
  nuclearInterest?: NuclearInterest;
  priority?: Priority;
}

const ARCHETYPE_DEFAULT_INTENT: Record<CompanyArchetype, ArchetypeDefaultIntent> = {
  hyperscaler:        { workloadProfile: 'ai-training',  nuclearInterest: 'considering', priority: 'speed' },
  colocation:         { workloadProfile: 'colocation',   nuclearInterest: 'no',          priority: 'speed' },
  developer:          { workloadProfile: 'mixed',        nuclearInterest: 'considering', priority: 'cost' },
  utility:            { workloadProfile: 'enterprise',   nuclearInterest: 'primary',     priority: 'resilience' },
  ipp:                { workloadProfile: 'enterprise',   nuclearInterest: 'considering', priority: 'cost' },
  'energy-major':     { workloadProfile: 'enterprise',   nuclearInterest: 'primary',     priority: 'cost' },
  sovereign:          { workloadProfile: 'hpc',          nuclearInterest: 'primary',     priority: 'resilience' },
  consultancy:        { workloadProfile: 'mixed',        nuclearInterest: 'considering', priority: 'cost' },
  'financial-sponsor':{ workloadProfile: 'mixed',        nuclearInterest: 'considering', priority: 'cost' },
  'owner-operator':   { workloadProfile: 'enterprise',   nuclearInterest: 'considering', priority: 'resilience' },
  epc:                { workloadProfile: 'mixed',        nuclearInterest: 'considering', priority: 'speed' },
  oem:                { workloadProfile: 'mixed',        nuclearInterest: 'primary',     priority: 'speed' },
  research:           { workloadProfile: 'hpc',          nuclearInterest: 'primary',     priority: 'resilience' },
  other:              {},
};

const ARCHETYPE_LABEL: Record<CompanyArchetype, string> = {
  hyperscaler: 'hyperscaler',
  colocation: 'colocation operator',
  developer: 'developer',
  utility: 'utility',
  ipp: 'IPP',
  'energy-major': 'energy major',
  sovereign: 'sovereign / government',
  consultancy: 'consultancy',
  'financial-sponsor': 'financial sponsor',
  'owner-operator': 'owner-operator',
  epc: 'EPC firm',
  oem: 'OEM',
  research: 'research institution',
  other: 'organisation',
};

// ─────────────────────────────────────────────────────────────────────────────
// IP6 — document-text parser is now in `./intentDocExtractor`. We use a
// short local alias so the per-field resolvers below read cleanly.
// ─────────────────────────────────────────────────────────────────────────────

type DocExtraction = DocIntentExtraction;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function clip(s: string, max = 140): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

function daysAgo(iso: string | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const diff = Date.now() - t;
  if (diff < 0) return 0;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function priorReportPhrase(report: SiteRagReport): string {
  const d = daysAgo(report.createdAt);
  if (d == null) return 'a prior report';
  if (d === 0) return 'a prior report from today';
  if (d === 1) return 'a prior report from yesterday';
  if (d < 30) return `a prior report ${d} days ago`;
  if (d < 365) return `a prior report ${Math.floor(d / 30)} months ago`;
  return `a prior report ${Math.floor(d / 365)}+ years ago`;
}

/** Most-recent report whose projectIntent[field] is a non-`'undecided'` value.
 *  Reads from `report.rerunConfig.projectIntent` — that's where the wizard
 *  snapshots the intent at generation time. */
function findPriorIntent<K extends keyof ProjectIntent>(
  reports: SiteRagReport[] | undefined,
  field: K,
): { report: SiteRagReport; value: NonNullable<ProjectIntent[K]> } | null {
  if (!reports || reports.length === 0) return null;
  for (const r of reports) {
    const intent = r.rerunConfig?.projectIntent;
    const v = intent ? intent[field] : undefined;
    if (v != null && v !== 'undecided') {
      return { report: r, value: v as NonNullable<ProjectIntent[K]> };
    }
  }
  return null;
}

/**
 * S1 — portfolio-precedent cohort mode. Returns the most-common
 * non-`'undecided'` value for `field` across the precedent cohort, plus
 * the support count, or null when fewer than `MIN_PRECEDENT_COHORT`
 * entries carry a usable value. Deterministic tie-break: on a tie the
 * first value encountered in cohort order wins (cohort order is the
 * caller's similarity ranking), so the same cohort always yields the
 * same answer.
 */
function cohortMode<K extends keyof ProjectIntent>(
  cohort: Array<Partial<ProjectIntent>> | undefined,
  field: K,
): { value: NonNullable<ProjectIntent[K]>; support: number; total: number } | null {
  if (!cohort || cohort.length === 0) return null;
  const counts = new Map<NonNullable<ProjectIntent[K]>, number>();
  const order: NonNullable<ProjectIntent[K]>[] = [];
  let total = 0;
  for (const intent of cohort) {
    const v = intent[field];
    if (v == null || v === 'undecided') continue;
    total += 1;
    const key = v as NonNullable<ProjectIntent[K]>;
    if (!counts.has(key)) order.push(key);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  if (total < MIN_PRECEDENT_COHORT) return null;
  let best: NonNullable<ProjectIntent[K]> | null = null;
  let bestCount = 0;
  for (const key of order) {
    const c = counts.get(key) ?? 0;
    if (c > bestCount) {
      best = key;
      bestCount = c;
    }
  }
  if (best == null) return null;
  return { value: best, support: bestCount, total };
}

/** Resolve the nuclear-proximity hint — explicit input wins, else fall
 *  back to `site.evidenceReadiness`/SED snapshot if available. */
function resolveReactorKm(input: SuggestProjectIntentInput): number | null {
  if (typeof input.nearestReactorKm === 'number' && Number.isFinite(input.nearestReactorKm)) {
    return input.nearestReactorKm;
  }
  // No direct snapshot on SiteEntity for reactor distance; the caller is
  // expected to plumb it in when known. Returning null means "skip the
  // site-context tier for nuclear interest" rather than fabricating a
  // proximity.
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-field resolvers — each returns the FIRST non-fallback hit, or the
// fallback if none of the higher tiers fire.
// ─────────────────────────────────────────────────────────────────────────────

/** Cohort label for precedent rationales — caller's label wins, else a
 *  generic count phrase. Kept ≤140 chars by the `clip` at the call site. */
function cohortLabel(input: SuggestProjectIntentInput, total: number): string {
  if (input.precedentCohortLabel && input.precedentCohortLabel.trim()) {
    return input.precedentCohortLabel.trim();
  }
  return `${total} similar site${total === 1 ? '' : 's'}`;
}

/**
 * S1 — portfolio-precedent tier. Resolves `field`'s cohort mode and, when
 * present, returns a `portfolio-precedent` suggestion. Confidence scales
 * (55–69) with the share of the cohort that agreed, so a unanimous cohort
 * reads stronger than a bare-majority one — but always below the direct
 * site-context / document tiers and above archetype-default. Returns null
 * when the cohort is too thin (caller falls through to the next tier).
 */
function precedentSuggestion<K extends keyof ProjectIntent, V>(
  input: SuggestProjectIntentInput,
  field: K,
  noun: string,
): IntentSuggestion<V> | null {
  const mode = cohortMode(input.precedentIntents, field);
  if (!mode) return null;
  const share = mode.support / mode.total;
  const confidence = Math.round(55 + Math.min(1, Math.max(0, (share - 0.5) / 0.5)) * 14);
  const pct = Math.round(share * 100);
  return {
    value: mode.value as unknown as V,
    source: 'portfolio-precedent',
    rationale: clip(
      `${pct}% of ${cohortLabel(input, mode.total)} chose this ${noun}.`,
    ),
    confidence,
  };
}

function suggestNuclear(
  input: SuggestProjectIntentInput,
  docExtract: DocExtraction,
): IntentSuggestion<NuclearInterest> {
  // Tier 1 — prior report
  const prior = findPriorIntent(input.recentReports, 'nuclearInterest');
  if (prior) {
    return {
      value: prior.value as NuclearInterest,
      source: 'prior-report',
      rationale: clip(`Carried forward from ${priorReportPhrase(prior.report)}.`),
      confidence: 95,
    };
  }

  // Tier 2 — document-extracted
  if (docExtract.nuclearInterest) {
    const phrase = docExtract.matchedPhrase?.nuclearInterest ?? 'an intent phrase';
    return {
      value: docExtract.nuclearInterest,
      source: 'document-extracted',
      rationale: clip(`Sales deck / brief mentions "${phrase}".`),
      confidence: 85,
    };
  }

  // Tier 3 — site context (reactor proximity / ESP docs)
  const km = resolveReactorKm(input);
  if (input.hasEspDocs) {
    return {
      value: 'primary',
      source: 'site-context',
      rationale: clip('Site has Early Site Permit / ESP documents on file.'),
      confidence: 75,
    };
  }
  if (km != null) {
    if (km < 50) {
      return {
        value: 'primary',
        source: 'site-context',
        rationale: clip(`Within ${Math.round(km)} km of an NRC reactor — nuclear-adjacent.`),
        confidence: 72,
      };
    }
    if (km < 200) {
      return {
        value: 'considering',
        source: 'site-context',
        rationale: clip(`~${Math.round(km)} km from the nearest NRC reactor.`),
        confidence: 68,
      };
    }
    return {
      value: 'no',
      source: 'site-context',
      rationale: clip(`>${Math.round(km)} km from the nearest NRC reactor — out of cluster range.`),
      confidence: 65,
    };
  }

  // Tier 3.5 — portfolio precedent (in-cohort composite)
  const precedent = precedentSuggestion<'nuclearInterest', NuclearInterest>(
    input, 'nuclearInterest', 'nuclear stance',
  );
  if (precedent) return precedent;

  // Tier 4 — archetype default
  if (input.archetype) {
    const v = ARCHETYPE_DEFAULT_INTENT[input.archetype]?.nuclearInterest;
    if (v) {
      return {
        value: v,
        source: 'archetype-default',
        rationale: clip(`Typical for a ${ARCHETYPE_LABEL[input.archetype]} portfolio.`),
        confidence: 60,
      };
    }
  }

  // Tier 5 — DAM default
  if (input.archetype && input.damContext) {
    const v = input.damContext.defaultIntentByArchetype[input.archetype]?.nuclearInterest;
    if (v && v !== 'undecided') {
      return {
        value: v as NuclearInterest,
        source: 'dam-default',
        rationale: clip(`Datacrate Model default for ${ARCHETYPE_LABEL[input.archetype]}.`),
        confidence: 50,
      };
    }
  }

  // Tier 6 — fallback
  return {
    value: 'considering',
    source: 'fallback',
    rationale: clip('No signal — defaulting to keep nuclear open as an option.'),
    confidence: 30,
  };
}

function suggestPriority(
  input: SuggestProjectIntentInput,
  docExtract: DocExtraction,
): IntentSuggestion<Priority> {
  const prior = findPriorIntent(input.recentReports, 'priority');
  if (prior) {
    return {
      value: prior.value as Priority,
      source: 'prior-report',
      rationale: clip(`Carried forward from ${priorReportPhrase(prior.report)}.`),
      confidence: 95,
    };
  }

  if (docExtract.priority) {
    const phrase = docExtract.matchedPhrase?.priority ?? 'a priority phrase';
    return {
      value: docExtract.priority,
      source: 'document-extracted',
      rationale: clip(`Sales deck / brief mentions "${phrase}".`),
      confidence: 85,
    };
  }

  // Site context — light heuristic: if we have an ESG-leaning archetype
  // and nothing else, the archetype-default tier covers it. We don't
  // synthesise a priority from raw geography here; that's a future polish.

  // Tier 3.5 — portfolio precedent (in-cohort composite)
  const precedent = precedentSuggestion<'priority', Priority>(
    input, 'priority', 'priority',
  );
  if (precedent) return precedent;

  if (input.archetype) {
    const v = ARCHETYPE_DEFAULT_INTENT[input.archetype]?.priority;
    if (v) {
      return {
        value: v,
        source: 'archetype-default',
        rationale: clip(`Typical for a ${ARCHETYPE_LABEL[input.archetype]} portfolio.`),
        confidence: 60,
      };
    }
  }

  if (input.archetype && input.damContext) {
    const v = input.damContext.defaultIntentByArchetype[input.archetype]?.priority;
    if (v && v !== 'undecided') {
      return {
        value: v as Priority,
        source: 'dam-default',
        rationale: clip(`Datacrate Model default for ${ARCHETYPE_LABEL[input.archetype]}.`),
        confidence: 50,
      };
    }
  }

  return {
    value: 'cost',
    source: 'fallback',
    rationale: clip('No signal — defaulting to cost as the most common driver.'),
    confidence: 30,
  };
}

function suggestWorkload(
  input: SuggestProjectIntentInput,
  docExtract: DocExtraction,
): IntentSuggestion<WorkloadProfileId> {
  const prior = findPriorIntent(input.recentReports, 'workloadProfile');
  if (prior) {
    return {
      value: prior.value as WorkloadProfileId,
      source: 'prior-report',
      rationale: clip(`Carried forward from ${priorReportPhrase(prior.report)}.`),
      confidence: 95,
    };
  }

  if (docExtract.workloadProfile) {
    const phrase = docExtract.matchedPhrase?.workloadProfile ?? 'a workload phrase';
    return {
      value: docExtract.workloadProfile,
      source: 'document-extracted',
      rationale: clip(`Sales deck / brief mentions "${phrase}".`),
      confidence: 85,
    };
  }

  // Tier 3.5 — portfolio precedent (in-cohort composite)
  const precedent = precedentSuggestion<'workloadProfile', WorkloadProfileId>(
    input, 'workloadProfile', 'workload profile',
  );
  if (precedent) return precedent;

  if (input.archetype) {
    const v = ARCHETYPE_DEFAULT_INTENT[input.archetype]?.workloadProfile;
    if (v) {
      return {
        value: v,
        source: 'archetype-default',
        rationale: clip(`Typical for a ${ARCHETYPE_LABEL[input.archetype]} portfolio.`),
        confidence: 60,
      };
    }
  }

  if (input.archetype && input.damContext) {
    const v = input.damContext.defaultIntentByArchetype[input.archetype]?.workloadProfile;
    if (v) {
      return {
        value: v as WorkloadProfileId,
        source: 'dam-default',
        rationale: clip(`Datacrate Model default for ${ARCHETYPE_LABEL[input.archetype]}.`),
        confidence: 50,
      };
    }
  }

  return {
    value: 'mixed',
    source: 'fallback',
    rationale: clip('No signal — defaulting to a mixed workload profile.'),
    confidence: 30,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// S1 — broadened deterministic resolvers (cooling / heat-rejection /
// timeline / power). Each returns null when no tier fires (the field then
// stays absent on the result so the form value is left untouched).
// ─────────────────────────────────────────────────────────────────────────────

const POWER_FORM_MIN_MW = 0.1;
const POWER_FORM_MAX_MW = 10000;

/** Liquid-cooling workloads — high density profiles where CDU loops are
 *  the norm. Used to seed `coolingStrategy` when no explicit signal. */
const LIQUID_COOLED_WORKLOADS: ReadonlySet<WorkloadProfileId> = new Set([
  'ai-training',
  'hpc',
]);

/**
 * Resolve the effective workload signal the cooling / power tiers lean on:
 * an explicit caller hint wins, else the workload suggestion computed in
 * the same pass — but only when that suggestion is itself a real signal
 * (not the platform fallback), so we never cascade a guessed workload into
 * a guessed cooling choice.
 */
function effectiveWorkloadSignal(
  input: SuggestProjectIntentInput,
  workloadSuggestion: IntentSuggestion<WorkloadProfileId>,
): WorkloadProfileId | null {
  if (input.workloadProfileHint) return input.workloadProfileHint;
  if (workloadSuggestion.source !== 'fallback') return workloadSuggestion.value;
  return null;
}

function suggestCooling(
  input: SuggestProjectIntentInput,
  workload: WorkloadProfileId | null,
): IntentSuggestion<CoolingStrategy> | null {
  // Tier 1 — prior report
  const prior = findPriorIntent(input.recentReports, 'coolingStrategy');
  if (prior && prior.value !== 'undecided') {
    return {
      value: prior.value as CoolingStrategy,
      source: 'prior-report',
      rationale: clip(`Carried forward from ${priorReportPhrase(prior.report)}.`),
      confidence: 92,
    };
  }

  // Tier 3.5 — portfolio precedent
  const precedent = precedentSuggestion<'coolingStrategy', CoolingStrategy>(
    input, 'coolingStrategy', 'cooling strategy',
  );
  if (precedent && precedent.value !== ('undecided' as unknown)) return precedent;

  // Tier 3 — site/workload heuristic. High-density GPU/HPC workloads imply
  // liquid cooling; everything else with a known workload defaults to air.
  if (workload) {
    if (LIQUID_COOLED_WORKLOADS.has(workload)) {
      return {
        value: 'liquid',
        source: 'site-context',
        rationale: clip(`Dense ${workload} workload — liquid cooling (CDU loops) is typical.`),
        confidence: 66,
      };
    }
    if (workload === 'colocation') {
      return {
        value: 'hybrid',
        source: 'site-context',
        rationale: clip('Multi-tenant colocation — mixed tenant loads favour hybrid cooling.'),
        confidence: 60,
      };
    }
  }
  return null;
}

function suggestHeatRejection(
  input: SuggestProjectIntentInput,
): IntentSuggestion<BopHeatRejectionId> | null {
  // Tier 1 — prior report
  const prior = findPriorIntent(input.recentReports, 'bopHeatRejection');
  if (prior && prior.value !== 'unknown') {
    return {
      value: prior.value as BopHeatRejectionId,
      source: 'prior-report',
      rationale: clip(`Carried forward from ${priorReportPhrase(prior.report)}.`),
      confidence: 90,
    };
  }

  // Tier 3.5 — portfolio precedent
  const precedent = precedentSuggestion<'bopHeatRejection', BopHeatRejectionId>(
    input, 'bopHeatRejection', 'heat-rejection method',
  );
  if (precedent && precedent.value !== ('unknown' as unknown)) return precedent;

  // Tier 3 — climate heuristic. Water-stressed / hot-dry climates push to
  // dry coolers; abundant water + cooler climates allow wet towers (more
  // efficient where water is available). Needs the climate snapshot.
  const ws = input.climate?.waterStress;
  const t = input.climate?.avgTempC;
  if (ws === 'High') {
    return {
      value: 'dry-cooler',
      source: 'site-context',
      rationale: clip('High local water stress — dry coolers avoid consumptive water use.'),
      confidence: 64,
    };
  }
  if (ws === 'Low' && typeof t === 'number' && Number.isFinite(t)) {
    if (t < 12) {
      return {
        value: 'free-cooling',
        source: 'site-context',
        rationale: clip(`Cool climate (${Math.round(t)}°C avg) + ample water — free cooling is viable.`),
        confidence: 60,
      };
    }
    return {
      value: 'wet-cooling-tower',
      source: 'site-context',
      rationale: clip('Low water stress — wet cooling towers are the efficient cold-end choice.'),
      confidence: 60,
    };
  }
  return null;
}

/**
 * #157 — Deterministic cooling-GENERATION machine resolver. Names the MACHINE
 * that produces cooling, distinct from `suggestCooling` (IT-side strategy) and
 * `suggestHeatRejection` (cold-end sink). This makes the absorption / thermal
 * generation choice reachable via its OWN field instead of being smuggled into
 * `bopHeatRejection`.
 *
 * Tiers (returns null when no tier fires — the field then stays absent so the
 * form value is untouched):
 *   1. PRIOR REPORT — carried forward (duck-typed read; the field is additive).
 *   2. PORTFOLIO PRECEDENT — cohort mode.
 *   3. CONFIG HEURISTIC — on-site thermal/steam gen present AND a waste-heat
 *      reuse intent → `absorption-thermal` (drive cooling from the heat stream).
 *      On-site thermal/steam gen present but NO waste-heat intent → leave unset
 *      (the user hasn't decided to thermalise cooling). NO on-site thermal gen →
 *      `vapour-compression-electric` (only an electric chiller is feasible).
 *
 * Conservative: an `unknown` signal yields null (no suggestion).
 */
function suggestCoolingGeneration(
  input: SuggestProjectIntentInput,
): IntentSuggestion<CoolingGenerationId> | null {
  // Tier 1 — prior report. Duck-typed: `coolingGeneration` is an additive,
  // forward-compatible field, so read it without assuming the type declares it.
  const prior = findPriorIntent(
    input.recentReports,
    'coolingGeneration' as keyof ProjectIntent,
  );
  if (prior) {
    const pv = prior.value as unknown;
    if (
      pv === 'absorption-thermal' ||
      pv === 'steam-driven-mechanical' ||
      pv === 'vapour-compression-electric'
    ) {
      return {
        value: pv,
        source: 'prior-report',
        rationale: clip(`Carried forward from ${priorReportPhrase(prior.report)}.`),
        confidence: 90,
      };
    }
  }

  // Tier 3.5 — portfolio precedent.
  const precedent = precedentSuggestion<keyof ProjectIntent, CoolingGenerationId>(
    input,
    'coolingGeneration' as keyof ProjectIntent,
    'cooling-generation machine',
  );
  if (
    precedent &&
    (precedent.value === 'absorption-thermal' ||
      precedent.value === 'steam-driven-mechanical' ||
      precedent.value === 'vapour-compression-electric')
  ) {
    return precedent;
  }

  // Tier 3 — config heuristic. Requires an explicit on-site-thermal signal.
  if (input.hasOnSiteThermalGen === true) {
    if (input.wasteHeatReuseIntent === true) {
      return {
        value: 'absorption-thermal',
        source: 'site-context',
        rationale: clip(
          'On-site thermal generation + waste-heat reuse intent — absorption/CCHP cooling is the heat-usable machine.',
        ),
        confidence: 66,
      };
    }
    // Thermal gen present but no waste-heat reuse intent — the user has not
    // committed to thermalising cooling; leave the field unset (no guess).
    return null;
  }
  if (input.hasOnSiteThermalGen === false) {
    return {
      value: 'vapour-compression-electric',
      source: 'site-context',
      rationale: clip(
        'No on-site thermal generation — only an electric vapour-compression chiller is feasible.',
      ),
      confidence: 62,
    };
  }

  // Unknown thermal-gen signal — conservative, no suggestion.
  return null;
}

/** Suggest an explicitly declared owner load, never a site-capacity estimate. */
function suggestPowerMW(
  input: SuggestProjectIntentInput,
  docExtract: DocExtraction,
): IntentSuggestion<number> | null {
  // Tier 1 — prior report (exact MW only).
  const prior = findPriorIntent(input.recentReports, 'powerRequirementMW');
  if (prior && typeof prior.value === 'number' && Number.isFinite(prior.value)) {
    const mw = clampPowerMw(prior.value);
    if (mw != null) {
      return {
        value: mw,
        source: 'prior-report',
        rationale: clip(`Carried forward from ${priorReportPhrase(prior.report)} (${formatMw(mw)}).`),
        confidence: 90,
      };
    }
  }

  // Tier 2 — document-extracted (explicit "350 MW" in the deck).
  if (typeof docExtract.powerRequirementMW === 'number') {
    const mw = clampPowerMw(docExtract.powerRequirementMW);
    if (mw != null) {
      const phrase = docExtract.matchedPhrase?.powerRequirementMW ?? `${formatMw(mw)}`;
      return {
        value: mw,
        source: 'document-extracted',
        rationale: clip(`Sales deck / brief states "${phrase}".`),
        confidence: 85,
      };
    }
  }

  // Tier 3.5 — portfolio precedent (median of cohort exact-MW values).
  const cohortMw = precedentMedianMw(input.precedentIntents);
  if (cohortMw != null) {
    const mw = clampPowerMw(cohortMw.value);
    if (mw != null) {
      return {
        value: mw,
        source: 'portfolio-precedent',
        rationale: clip(`Median ${formatMw(mw)} across ${cohortLabel(input, cohortMw.total)}.`),
        confidence: 58,
      };
    }
  }

  // Parcel acreage and workload-density anchors do not establish a usable
  // campus capacity. Source-supported conditional ranges are displayed by the
  // governed screening recommendation surface, separate from owner Intent.

  return null;
}

function suggestTimeline(
  input: SuggestProjectIntentInput,
  docExtract: DocExtraction,
): { inServiceDate?: IntentSuggestion<string>; timeline?: IntentSuggestion<TimelineBucketId> } {
  // Tier 1 — prior report. Prefer the exact date, else the bucket.
  const priorDate = findPriorIntent(input.recentReports, 'inServiceDate');
  if (priorDate && typeof priorDate.value === 'string' && priorDate.value) {
    return {
      inServiceDate: {
        value: priorDate.value,
        source: 'prior-report',
        rationale: clip(`Carried forward from ${priorReportPhrase(priorDate.report)}.`),
        confidence: 92,
      },
    };
  }
  const priorBucket = findPriorIntent(input.recentReports, 'timeline');
  if (priorBucket && priorBucket.value !== 'undecided') {
    return {
      timeline: {
        value: priorBucket.value as TimelineBucketId,
        source: 'prior-report',
        rationale: clip(`Carried forward from ${priorReportPhrase(priorBucket.report)}.`),
        confidence: 90,
      },
    };
  }

  // Tier 2 — document-extracted (exact COD anchor, else relative bucket).
  if (docExtract.inServiceDate) {
    const phrase = docExtract.matchedPhrase?.timeline ?? docExtract.inServiceDate;
    return {
      inServiceDate: {
        value: docExtract.inServiceDate,
        source: 'document-extracted',
        rationale: clip(`Sales deck / brief targets "${phrase}".`),
        confidence: 84,
      },
    };
  }
  if (docExtract.timeline) {
    const phrase = docExtract.matchedPhrase?.timeline ?? docExtract.timeline;
    return {
      timeline: {
        value: docExtract.timeline,
        source: 'document-extracted',
        rationale: clip(`Sales deck / brief targets "${phrase}".`),
        confidence: 82,
      },
    };
  }

  // Tier 3.5 — portfolio precedent (cohort mode of the bucket).
  const precedent = precedentSuggestion<'timeline', TimelineBucketId>(
    input, 'timeline', 'build timeline',
  );
  if (precedent && precedent.value !== ('undecided' as unknown)) {
    return { timeline: precedent };
  }
  return {};
}

/** Clamp an MW value to the form's accepted bounds; null if out of range. */
function clampPowerMw(mw: number): number | null {
  if (!Number.isFinite(mw)) return null;
  if (mw < POWER_FORM_MIN_MW || mw > POWER_FORM_MAX_MW) return null;
  return mw;
}

/** Trim trailing zeros so 350 → "350 MW", 7.5 → "7.5 MW". Local copy to
 *  avoid a cross-module import for one formatting call. */
function formatMw(mw: number): string {
  const rounded = Math.round(mw * 10) / 10;
  const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString();
  return `${text} MW`;
}

/** Median exact-MW across a precedent cohort, or null when too few carry a
 *  usable figure. */
function precedentMedianMw(
  cohort: Array<Partial<ProjectIntent>> | undefined,
): { value: number; total: number } | null {
  if (!cohort || cohort.length === 0) return null;
  const values = cohort
    .map((c) => c.powerRequirementMW)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0)
    .sort((a, b) => a - b);
  if (values.length < MIN_PRECEDENT_COHORT) return null;
  const mid = Math.floor(values.length / 2);
  const median = values.length % 2 === 0
    ? (values[mid - 1] + values[mid]) / 2
    : values[mid];
  return { value: Math.round(median), total: values.length };
}

// ─────────────────────────────────────────────────────────────────────────────
// #34 — DECIDE-NEXT BTM decision-field resolvers. These mirror the tiering of
// the spine fields (prior-report → document-extracted → site/intent-context →
// portfolio-precedent → archetype → fallback) for the four DECIDE-NEXT fields
// that previously had NO inferred suggestion: capacityCreditIntent (ELCC),
// offtake (PPA), carbonCommitment, and reactorDesignClass.
//
// Each ALWAYS returns a suggestion (never null) so the UI can show a decide
// nudge, but emits `source: 'fallback'` (so the UI can hide it) UNLESS a real
// signal fired. Conservative: a non-fallback tier requires a concrete signal
// (a nuclear stance, a sustainability priority, a carbon-conscious archetype,
// an explicit prior/document value, or a precedent cohort).
// ─────────────────────────────────────────────────────────────────────────────

/** Archetypes whose portfolios are typically carbon-conscious (RE100 / CFE /
 *  net-zero pressure). Used to seed `carbonCommitment` from the archetype when
 *  no stronger signal exists. */
const CARBON_CONSCIOUS_ARCHETYPES: ReadonlySet<CompanyArchetype> = new Set([
  'hyperscaler',
  'colocation',
  'utility',
  'energy-major',
  'sovereign',
]);

/**
 * Resolve the effective nuclear stance the reactor-class / ELCC tiers lean on:
 * an explicit caller hint (the USER's declared stance) wins, else the nuclear
 * suggestion computed in the same pass — but only when that suggestion is a
 * real signal (not the platform fallback), so we never cascade a guessed
 * nuclear stance into a guessed reactor class. `'undecided'` is not a signal.
 */
function effectiveNuclearSignal(
  input: SuggestProjectIntentInput,
  nuclearSuggestion: IntentSuggestion<NuclearInterest>,
): NuclearInterest | null {
  const hint = input.nuclearInterestHint;
  if (hint && hint !== ('undecided' as unknown as NuclearInterest)) return hint;
  if (nuclearSuggestion.source !== 'fallback') return nuclearSuggestion.value;
  return null;
}

/**
 * #34 — effective priority signal: an explicit caller hint (the USER's
 * declared priority) wins, else the priority suggestion computed in the same
 * pass when it is a real (non-fallback) signal. Returns null when neither is a
 * usable signal, so the offtake / carbon / ELCC tiers don't lean on a guess.
 */
function effectivePrioritySignal(
  input: SuggestProjectIntentInput,
  prioritySuggestion: IntentSuggestion<Priority>,
): Priority | null {
  const hint = input.priorityHint;
  if (hint && hint !== ('undecided' as unknown as Priority)) return hint;
  if (prioritySuggestion.source !== 'fallback') return prioritySuggestion.value;
  return null;
}

/**
 * #34 — Firm-capacity (ELCC) accreditation target. Higher ELCC ambition is
 * implied when the project leans on firm on-site generation (nuclear primary)
 * or prioritises resilience; a sustainability-led merchant build leans lower.
 */
function suggestCapacityCredit(
  input: SuggestProjectIntentInput,
  nuclear: NuclearInterest | null,
  priority: Priority | null,
): IntentSuggestion<CapacityCreditTargetId> {
  // Tier 1 — prior report. The field is an object; read its `.target`.
  const prior = findPriorIntent(input.recentReports, 'capacityCreditIntent');
  if (prior && prior.value && typeof prior.value === 'object') {
    const t = (prior.value as { target?: unknown }).target;
    if (t === 'none' || t === 'partial' || t === 'firm') {
      return {
        value: t,
        source: 'prior-report',
        rationale: clip(`Carried forward from ${priorReportPhrase(prior.report)}.`),
        confidence: 90,
      };
    }
  }

  // Tier 3.5 — portfolio precedent (cohort mode of `.target`).
  const precedent = capacityCreditPrecedent(input);
  if (precedent) return precedent;

  // Tier 3 — intent/site signal. Firm on-site nuclear ⇒ a firm-capacity target
  // is the headline BTM ambition; resilience priority ⇒ at least partial.
  if (nuclear === 'primary') {
    return {
      value: 'firm',
      source: 'site-context',
      rationale: clip('Nuclear-led firm baseload — a firm-capacity (ELCC) accreditation target is the headline BTM ambition.'),
      confidence: 68,
    };
  }
  if (priority === 'resilience') {
    return {
      value: 'firm',
      source: 'site-context',
      rationale: clip('Resilience-led project — a firm capacity-credit target underpins the availability case.'),
      confidence: 62,
    };
  }
  if (nuclear === 'considering') {
    return {
      value: 'partial',
      source: 'site-context',
      rationale: clip('Nuclear under consideration — a partial firm-capacity target is a defensible interim ELCC stance.'),
      confidence: 58,
    };
  }

  // Tier 6 — fallback. No signal — leave the ELCC target open.
  return {
    value: 'none',
    source: 'fallback',
    rationale: clip('No signal — defaulting to no declared firm-capacity (ELCC) target.'),
    confidence: 30,
  };
}

/** #34 — cohort mode of the nested `capacityCreditIntent.target`. */
function capacityCreditPrecedent(
  input: SuggestProjectIntentInput,
): IntentSuggestion<CapacityCreditTargetId> | null {
  const targets = (input.precedentIntents ?? [])
    .map((c) => c.capacityCreditIntent?.target)
    .filter((t): t is CapacityCreditTargetId => t === 'none' || t === 'partial' || t === 'firm');
  return nestedCohortSuggestion<CapacityCreditTargetId>(input, targets, 'firm-capacity target');
}

/**
 * #34 — Carbon commitment. A sustainability priority or a carbon-conscious
 * archetype implies a clean-energy commitment (24/7-CFE is the strongest
 * defensible default for a firm-baseload BTM build); otherwise leave open.
 */
function suggestCarbonCommitment(
  input: SuggestProjectIntentInput,
  priority: Priority | null,
): IntentSuggestion<CarbonCommitmentId> {
  // Tier 1 — prior report.
  const prior = findPriorIntent(input.recentReports, 'carbonCommitment');
  if (prior && isCarbonCommitmentId(prior.value)) {
    return {
      value: prior.value,
      source: 'prior-report',
      rationale: clip(`Carried forward from ${priorReportPhrase(prior.report)}.`),
      confidence: 90,
    };
  }

  // Tier 3.5 — portfolio precedent.
  const precedent = carbonCommitmentPrecedent(input);
  if (precedent) return precedent;

  // Tier 3 — priority signal. A sustainability-led project carries a clean
  // commitment; 24/7-CFE is the most defensible posture for a firm BTM build.
  if (priority === 'sustainability') {
    return {
      value: '24-7-cfe',
      source: 'site-context',
      rationale: clip('Sustainability-led project — 24/7 carbon-free energy is the strongest defensible commitment.'),
      confidence: 64,
    };
  }

  // Tier 4 — archetype default. Carbon-conscious portfolios typically carry an
  // RE100-class commitment even absent an explicit sustainability priority.
  if (input.archetype && CARBON_CONSCIOUS_ARCHETYPES.has(input.archetype)) {
    return {
      value: 're100',
      source: 'archetype-default',
      rationale: clip(`Typical for a ${ARCHETYPE_LABEL[input.archetype]} portfolio — RE100-class commitment.`),
      confidence: 56,
    };
  }

  // Tier 6 — fallback.
  return {
    value: 'none',
    source: 'fallback',
    rationale: clip('No signal — defaulting to no declared carbon commitment.'),
    confidence: 30,
  };
}

/** #34 — cohort mode of `carbonCommitment` (flat enum). */
function carbonCommitmentPrecedent(
  input: SuggestProjectIntentInput,
): IntentSuggestion<CarbonCommitmentId> | null {
  const values = (input.precedentIntents ?? [])
    .map((c) => c.carbonCommitment)
    .filter((v): v is CarbonCommitmentId => isCarbonCommitmentId(v) && v !== 'none');
  return nestedCohortSuggestion<CarbonCommitmentId>(input, values, 'carbon commitment');
}

function isCarbonCommitmentId(v: unknown): v is CarbonCommitmentId {
  return v === 'none' || v === 're100' || v === '24-7-cfe'
    || v === 'net-zero-2030' || v === 'net-zero-2050' || v === 'custom';
}

/**
 * #34 — Reactor design class. Only meaningful once nuclear is in play; we never
 * suggest a reactor class for a non-nuclear project (returns `fallback` so the
 * UI hides it). When nuclear IS in play, an LWR-based SMR is the most common /
 * defensible site-level default for a BTM datacenter build.
 */
function suggestReactorClass(
  input: SuggestProjectIntentInput,
  nuclear: NuclearInterest | null,
): IntentSuggestion<ReactorDesignClassSuggestionId> {
  // Tier 1 — prior report.
  const prior = findPriorIntent(input.recentReports, 'reactorDesignClass');
  if (prior && isReactorDesignClassId(prior.value) && prior.value !== 'undecided') {
    return {
      value: prior.value,
      source: 'prior-report',
      rationale: clip(`Carried forward from ${priorReportPhrase(prior.report)}.`),
      confidence: 90,
    };
  }

  // Tier 3.5 — portfolio precedent.
  const precedent = reactorClassPrecedent(input);
  if (precedent) return precedent;

  // Tier 3 — intent signal. Only fire when nuclear is a real, in-play signal.
  if (nuclear === 'primary' || nuclear === 'considering') {
    const conf = nuclear === 'primary' ? 60 : 55;
    return {
      value: 'lwr-smr',
      source: 'site-context',
      rationale: clip('Nuclear in play — an LWR-based SMR is the most common, lowest-licensing-risk site-level default.'),
      confidence: conf,
    };
  }

  // Tier 6 — fallback. No nuclear signal — no reactor class to suggest.
  return {
    value: 'undecided',
    source: 'fallback',
    rationale: clip('No nuclear signal — reactor design class is not yet applicable.'),
    confidence: 30,
  };
}

/** #34 — cohort mode of `reactorDesignClass` (skips `undecided`). */
function reactorClassPrecedent(
  input: SuggestProjectIntentInput,
): IntentSuggestion<ReactorDesignClassSuggestionId> | null {
  const values = (input.precedentIntents ?? [])
    .map((c) => c.reactorDesignClass)
    .filter((v): v is ReactorDesignClassSuggestionId => isReactorDesignClassId(v) && v !== 'undecided');
  return nestedCohortSuggestion<ReactorDesignClassSuggestionId>(input, values, 'reactor design class');
}

function isReactorDesignClassId(v: unknown): v is ReactorDesignClassSuggestionId {
  return v === 'lwr-smr' || v === 'htgr' || v === 'molten-salt'
    || v === 'microreactor' || v === 'large-lwr' || v === 'undecided';
}

/**
 * #34 — Shared cohort-mode helper for the nested / flat BTM enums. Takes the
 * already-extracted list of usable values (caller does the nested read) and
 * returns a `portfolio-precedent` suggestion when at least
 * `MIN_PRECEDENT_COHORT` entries agree on a mode, mirroring
 * `precedentSuggestion`'s confidence scaling (55–69). Deterministic
 * first-seen tie-break, like `cohortMode`.
 */
function nestedCohortSuggestion<V extends string>(
  input: SuggestProjectIntentInput,
  values: V[],
  noun: string,
): IntentSuggestion<V> | null {
  const total = values.length;
  if (total < MIN_PRECEDENT_COHORT) return null;
  const counts = new Map<V, number>();
  const order: V[] = [];
  for (const v of values) {
    if (!counts.has(v)) order.push(v);
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  let best: V | null = null;
  let bestCount = 0;
  for (const v of order) {
    const c = counts.get(v) ?? 0;
    if (c > bestCount) {
      best = v;
      bestCount = c;
    }
  }
  if (best == null) return null;
  const share = bestCount / total;
  const confidence = Math.round(55 + Math.min(1, Math.max(0, (share - 0.5) / 0.5)) * 14);
  const pct = Math.round(share * 100);
  return {
    value: best,
    source: 'portfolio-precedent',
    rationale: clip(`${pct}% of ${cohortLabel(input, total)} chose this ${noun}.`),
    confidence,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Public entry point
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute per-field intent suggestions from whatever context the caller
 * has. Never throws — missing tiers are skipped silently.
 */
export function suggestProjectIntent(
  input: SuggestProjectIntentInput,
): SuggestedProjectIntent {
  const docExtract = extractFromDocsImpl(input.documents);

  const workloadProfile = suggestWorkload(input, docExtract);
  const nuclearInterest = suggestNuclear(input, docExtract);
  const priority = suggestPriority(input, docExtract);

  // S1 — broadened deterministic coverage. Cooling / power lean on the
  // workload signal resolved in the same pass (never a guessed workload).
  const workloadSignal = effectiveWorkloadSignal(input, workloadProfile);
  const coolingStrategy = suggestCooling(input, workloadSignal) ?? undefined;
  const bopHeatRejection = suggestHeatRejection(input) ?? undefined;
  // #157 — cooling-GENERATION machine, separate from the IT-side strategy AND
  // the cold-end sink suggesters above (which are intentionally unchanged).
  const coolingGeneration = suggestCoolingGeneration(input) ?? undefined;
  const powerRequirementMW = suggestPowerMW(input, docExtract) ?? undefined;
  const timelineResult = suggestTimeline(input, docExtract);

  // #34 — DECIDE-NEXT BTM decision fields. Lean on the nuclear / priority
  // signals resolved in the same pass (an explicit USER hint wins, else the
  // computed suggestion when it's a real signal — never a guessed stance),
  // mirroring how cooling / power lean on the workload signal.
  const nuclearSignal = effectiveNuclearSignal(input, nuclearInterest);
  const prioritySignal = effectivePrioritySignal(input, priority);
  const capacityCreditIntent = suggestCapacityCredit(input, nuclearSignal, prioritySignal);
  const carbonCommitment = suggestCarbonCommitment(input, prioritySignal);
  const reactorDesignClass = suggestReactorClass(input, nuclearSignal);

  const result: SuggestedProjectIntent = {
    workloadProfile,
    nuclearInterest,
    priority,
  };
  // Only attach the optional fields that actually produced a suggestion,
  // and never attach one for a field the user already authored
  // (USER ALWAYS WINS — provenance-gated).
  const ci = input.currentIntent;
  if (coolingStrategy && !isUserAuthored(ci, 'coolingStrategy')) {
    result.coolingStrategy = coolingStrategy;
  }
  if (bopHeatRejection && !isUserAuthored(ci, 'bopHeatRejection')) {
    result.bopHeatRejection = bopHeatRejection;
  }
  // #157 — duck-typed provenance key (the field is additive). USER WINS.
  if (
    coolingGeneration &&
    !isUserAuthored(ci, 'coolingGeneration' as keyof NonNullable<ProjectIntent['fieldSource']>)
  ) {
    result.coolingGeneration = coolingGeneration;
  }
  if (powerRequirementMW
    && !isUserAuthored(ci, 'powerRequirementMW')
    && !isUserAuthored(ci, 'powerRequirement')) {
    result.powerRequirementMW = powerRequirementMW;
  }
  if (timelineResult.inServiceDate
    && !isUserAuthored(ci, 'inServiceDate')
    && !isUserAuthored(ci, 'timeline')) {
    result.inServiceDate = timelineResult.inServiceDate;
  }
  if (timelineResult.timeline
    && !isUserAuthored(ci, 'timeline')
    && !isUserAuthored(ci, 'inServiceDate')) {
    result.timeline = timelineResult.timeline;
  }
  // #34 — optional detailed-planning suggestions (offtake is never inferred).
  // The remaining fields are attached (the UI reads `source` and
  // hides a `'fallback'` suggestion), but never when the user authored the
  // field (USER ALWAYS WINS — provenance-gated, mirroring the fields above).
  if (!isUserAuthored(ci, 'capacityCreditIntent')) {
    result.capacityCreditIntent = capacityCreditIntent;
  }
  if (!isUserAuthored(ci, 'carbonCommitment')) {
    result.carbonCommitment = carbonCommitment;
  }
  if (!isUserAuthored(ci, 'reactorDesignClass')) {
    result.reactorDesignClass = reactorDesignClass;
  }
  return result;
}

/** Convenience predicate — true when at least one field's suggestion did
 *  NOT come from the fallback tier (i.e. there's something worth showing). */
export function hasNonFallbackSuggestions(s: SuggestedProjectIntent): boolean {
  return s.workloadProfile.source !== 'fallback'
      || s.nuclearInterest.source !== 'fallback'
      || s.priority.source !== 'fallback';
}

// ─────────────────────────────────────────────────────────────────────────────
// IP4 — provenance read helpers. Admin views read these to render
// "auto-suggested (source: prior-report)" markers next to each field
// without re-importing the source enum.
// ─────────────────────────────────────────────────────────────────────────────

/** Human-readable label for an IntentSuggestionSource — used by admin /
 *  diff sidebar surfaces. */
export const INTENT_SOURCE_LABEL: Record<IntentSuggestionSource, string> = {
  'prior-report': 'Prior report',
  'document-extracted': 'Sales deck',
  'site-context': 'Site context',
  'portfolio-precedent': 'Similar sites',
  'llm-inferred': 'AI inference',
  'archetype-default': 'Archetype default',
  'dam-default': 'Datacrate Model',
  'fallback': 'Platform default',
};

/** Alias kept for callers that import the shorter name per the design's
 *  REUSE note (INTENT_SOURCE_LABEL / SOURCE_LABEL). Same map, one source
 *  of truth — adding a tier above updates both. */
export const SOURCE_LABEL = INTENT_SOURCE_LABEL;

/** Returns the provenance tier for a given ProjectIntent field, or null if
 *  the user picked the value manually (no chip was accepted). Pure read
 *  helper — the value lives on `intent.fieldSource[field]`. */
export function getProjectIntentSource(
  intent: { fieldSource?: Partial<Record<string, string>> } | undefined,
  field:
    | 'workloadProfile'
    | 'nuclearInterest'
    | 'priority'
    | 'coolingStrategy'
    | 'bopHeatRejection'
    | 'timeline'
    | 'inServiceDate'
    | 'powerRequirementMW'
    | 'powerRequirement',
): IntentSuggestionSource | null {
  if (!intent?.fieldSource) return null;
  const v = intent.fieldSource[field];
  if (!v) return null;
  // Type narrow — only return known tiers; unknown values fall through to
  // null so legacy data with hand-written strings doesn't crash readers.
  if (v === 'prior-report' || v === 'document-extracted' || v === 'site-context'
      || v === 'portfolio-precedent' || v === 'llm-inferred'
      || v === 'archetype-default' || v === 'dam-default' || v === 'fallback') {
    return v;
  }
  return null;
}
